/**
 * Archetype-specific content for the redesigned Results page.
 * Populated from the client-supplied "result page_Content file.docx".
 *
 * Each archetype has:
 *  - tagline: 3-line descriptor (line 1 bold, lines 2-3 italic)
 *  - stoodOut: 3 bullets for "Here's what stood out about your answers"
 *  - traits: 5 traits + percentage for the horizontal bar chart
 *  - strengths: array of positives (green ticks)
 *  - weaknesses: array of negatives (red crosses)
 *  - shared: brand & character example folder names inside
 *      /public/archetype-examples/{key}/brands|characters/{1,2}.{ext}
 */

// helper: map display name -> folder-safe key (lowercase, ascii only)
export const archetypeKey = (name) => (name || '').toLowerCase().trim();

/**
 * Public URL builders — we ship 2 brand + 2 character images per archetype.
 * File extensions vary, so we resolve the actual filename at render time via a
 * pre-generated manifest (see archetypeImageManifest below).
 */
export const archetypeImageManifest = {
  caregiver:  { brands: ['1.png', '2.png'],  characters: ['1.png', '2.jpeg'] },
  creator:    { brands: ['1.png', '2.png'],  characters: ['1.jpg', '2.jpg'] },
  everyman:   { brands: ['1.png', '2.png'],  characters: ['1.jpeg', '2.jpg'] },
  explorer:   { brands: ['1.png', '2.png'],  characters: ['1.jpg', '2.png'] },
  hero:       { brands: ['1.png', '2.png'],  characters: ['1.jpg', '2.jpg'] },
  innocent:   { brands: ['1.png', '2.png'],  characters: ['1.jpg', '2.jpg'] },
  jester:     { brands: ['1.png', '2.png'],  characters: ['1.jpeg', '2.jpg'] },
  lover:      { brands: ['1.png', '2.png'],  characters: ['1.jpg', '2.jpg'] },
  magician:   { brands: ['1.png', '2.png'],  characters: ['1.jpeg', '2.jpg'] },
  rebel:      { brands: ['1.png', '2.png'],  characters: ['1.jpg', '2.jpg'] },
  ruler:      { brands: ['1.png', '2.png'],  characters: ['1.jpeg', '1.jpeg'] },
  sage:       { brands: ['1.png', '2.jpeg'], characters: ['1.jpg', '2.jpg'] },
};

export const getArchetypeExamples = (name) => {
  const key = archetypeKey(name);
  const m = archetypeImageManifest[key];
  if (!m) return [];
  return [
    { type: 'brand',     src: `/archetype-examples/${key}/brands/${m.brands[0]}` },
    { type: 'brand',     src: `/archetype-examples/${key}/brands/${m.brands[1]}` },
    { type: 'character', src: `/archetype-examples/${key}/characters/${m.characters[0]}` },
    { type: 'character', src: `/archetype-examples/${key}/characters/${m.characters[1]}` },
  ];
};

export const getArchetypeCard = (name) => `/archetype-cards/${archetypeKey(name)}.png`;

export const archetypeResultContent = {
  Rebel: {
    tagline: {
      lineBold: "The Rule Breaker.",
      lineItalic1: "It doesn't fit into categories.",
      lineItalic2: "It redefines them.",
    },
    stoodOut: [
      "You consistently chose originality over convention.",
      "You prioritise bold differentiation over familiarity.",
      "You believe brands should lead rather than follow.",
    ],
    traits: [
      { name: 'Fearless',     pct: 90 },
      { name: 'Independent',  pct: 70 },
      { name: 'Provocative',  pct: 30 },
      { name: 'Creative',     pct: 50 },
      { name: 'Conventional', pct: 70 },
    ],
    strengths:  ['Creates Loyal Fans', 'Commands Attention', 'Challenges Categories', 'Builds Distinctive Brands'],
    weaknesses: ['Too niche', 'Too aggressive', 'Difficult to understand', 'Inconsistent'],
  },
  Innocent: {
    tagline: {
      lineBold: "The Optimist.",
      lineItalic1: "It believes in the good.",
      lineItalic2: "It brings simplicity to a complicated world.",
    },
    stoodOut: [
      "You consistently chose honesty over hype.",
      "You value trust more than trends.",
      "You believe brands should make people feel safe, hopeful and optimistic.",
    ],
    traits: [
      { name: 'Optimistic',  pct: 95 },
      { name: 'Trustworthy', pct: 90 },
      { name: 'Simple',      pct: 85 },
      { name: 'Wholesome',   pct: 75 },
      { name: 'Playful',     pct: 40 },
    ],
    strengths:  ['Builds Trust Quickly', 'Creates Emotional Comfort', 'Timeless Appeal', 'Easy to Love'],
    weaknesses: ['Can Feel Too Safe', 'Lacks Excitement', 'Easily Copied', 'Avoids Difficult Conversations'],
  },
  Sage: {
    tagline: {
      lineBold: "The Thinker.",
      lineItalic1: "It seeks the truth.",
      lineItalic2: "It helps people make better decisions.",
    },
    stoodOut: [
      "You consistently chose knowledge over assumptions.",
      "You value expertise over popularity.",
      "You believe brands should educate before they persuade.",
    ],
    traits: [
      { name: 'Knowledgeable', pct: 95 },
      { name: 'Trustworthy',   pct: 90 },
      { name: 'Analytical',    pct: 85 },
      { name: 'Thoughtful',    pct: 75 },
      { name: 'Authoritative', pct: 70 },
    ],
    strengths:  ['Builds Credibility', 'Creates Long-term Authority', 'Makes Complex Things Simple', 'Inspires Confidence'],
    weaknesses: ['Can Feel Too Serious', 'Information Overload', 'Appears Distant', 'Slow to Innovate'],
  },
  Explorer: {
    tagline: {
      lineBold: "The Adventurer.",
      lineItalic1: "It refuses to stay within boundaries.",
      lineItalic2: "It inspires people to discover what's possible.",
    },
    stoodOut: [
      "You consistently chose freedom over familiarity.",
      "You believe growth comes from exploration.",
      "You value authenticity over conformity.",
    ],
    traits: [
      { name: 'Adventurous', pct: 95 },
      { name: 'Independent', pct: 90 },
      { name: 'Curious',     pct: 80 },
      { name: 'Bold',        pct: 75 },
      { name: 'Ambitious',   pct: 70 },
    ],
    strengths:  ['Encourages Discovery', 'Creates Aspirational Appeal', 'Highly Authentic', 'Inspires Action'],
    weaknesses: ['Can Feel Directionless', 'Appeals to a Niche', 'May Overcomplicate Choices', 'Difficult to Scale'],
  },
  Hero: {
    tagline: {
      lineBold: "The Champion.",
      lineItalic1: "It rises to every challenge.",
      lineItalic2: "It inspires others to become their best.",
    },
    stoodOut: [
      "You consistently chose courage over comfort.",
      "You believe brands should empower people to succeed.",
      "You value resilience, discipline and achievement.",
    ],
    traits: [
      { name: 'Courageous',  pct: 95 },
      { name: 'Determined',  pct: 90 },
      { name: 'Confident',   pct: 85 },
      { name: 'Reliable',    pct: 75 },
      { name: 'Competitive', pct: 70 },
    ],
    strengths:  ['Inspires Confidence', 'Motivates Action', 'Builds Respect', 'Creates Strong Loyalty'],
    weaknesses: ['Can Feel Intimidating', 'Too Performance-focused', 'Risks Perfectionism', 'May Appear Aggressive'],
  },
  Caregiver: {
    tagline: {
      lineBold: "The Protector.",
      lineItalic1: "It puts people first.",
      lineItalic2: "It exists to care, nurture and support.",
    },
    stoodOut: [
      "You consistently chose empathy over efficiency.",
      "You believe brands should make people feel cared for.",
      "You value trust, compassion and long-term relationships over quick wins.",
    ],
    traits: [
      { name: 'Compassionate', pct: 95 },
      { name: 'Reliable',      pct: 90 },
      { name: 'Supportive',    pct: 85 },
      { name: 'Trustworthy',   pct: 80 },
      { name: 'Selfless',      pct: 70 },
    ],
    strengths:  ['Builds Deep Trust', 'Creates Emotional Loyalty', 'Reassures Customers', 'Puts Customers First'],
    weaknesses: ['Can Feel Overprotective', 'Lacks Excitement', 'Avoids Taking Risks', 'Can Be Taken for Granted'],
  },
  Creator: {
    tagline: {
      lineBold: "The Visionary.",
      lineItalic1: "It imagines what doesn't exist.",
      lineItalic2: "It brings new ideas into the world.",
    },
    stoodOut: [
      "You consistently chose originality over imitation.",
      "You believe every brand should express a unique point of view.",
      "You value imagination, innovation and craftsmanship.",
    ],
    traits: [
      { name: 'Creative',   pct: 95 },
      { name: 'Visionary',  pct: 90 },
      { name: 'Expressive', pct: 85 },
      { name: 'Innovative', pct: 80 },
      { name: 'Original',   pct: 75 },
    ],
    strengths:  ['Stands Apart', 'Inspires Innovation', 'Builds Distinctive Identity', 'Encourages Self-expression'],
    weaknesses: ['Can Become Overly Complex', 'Perfection Delays Progress', 'Risks Being Niche', 'Can Lose Focus'],
  },
  Lover: {
    tagline: {
      lineBold: "The Romantic.",
      lineItalic1: "It creates meaningful connections.",
      lineItalic2: "It makes people feel desired, valued and special.",
    },
    stoodOut: [
      "You consistently chose emotion over functionality.",
      "You believe people remember how a brand makes them feel.",
      "You value beauty, intimacy and meaningful relationships.",
    ],
    traits: [
      { name: 'Passionate', pct: 95 },
      { name: 'Warm',       pct: 90 },
      { name: 'Elegant',    pct: 85 },
      { name: 'Empathetic', pct: 80 },
      { name: 'Sensual',    pct: 70 },
    ],
    strengths:  ['Creates Emotional Connections', 'Builds Brand Desire', 'Elevates Everyday Moments', 'Encourages Loyalty'],
    weaknesses: ['Can Feel Exclusive', 'Emotion Over Function', 'Difficult to Maintain', 'Risks Becoming Indulgent'],
  },
  Everyman: {
    tagline: {
      lineBold: "The Friend.",
      lineItalic1: "It makes everyone feel welcome.",
      lineItalic2: "It believes the best brands are the most relatable.",
    },
    stoodOut: [
      "You consistently chose authenticity over exclusivity.",
      "You believe brands should feel approachable and genuine.",
      "You value honesty, simplicity and a sense of belonging.",
    ],
    traits: [
      { name: 'Relatable', pct: 95 },
      { name: 'Honest',    pct: 90 },
      { name: 'Friendly',  pct: 85 },
      { name: 'Practical', pct: 80 },
      { name: 'Inclusive', pct: 75 },
    ],
    strengths:  ['Easy to Relate To', 'Builds Community', 'Appeals to a Wide Audience', 'Earns Everyday Trust'],
    weaknesses: ['Can Blend In', 'Lacks Premium Appeal', 'Avoids Bold Positions', 'Difficult to Differentiate'],
  },
  Jester: {
    tagline: {
      lineBold: "The Entertainer.",
      lineItalic1: "It doesn't just sell.",
      lineItalic2: "It makes people smile.",
    },
    stoodOut: [
      "You consistently chose fun over formality.",
      "You believe brands should entertain as much as they engage.",
      "You value spontaneity, humour and memorable experiences.",
    ],
    traits: [
      { name: 'Playful',     pct: 95 },
      { name: 'Witty',       pct: 90 },
      { name: 'Energetic',   pct: 85 },
      { name: 'Spontaneous', pct: 80 },
      { name: 'Irreverent',  pct: 70 },
    ],
    strengths:  ['Highly Memorable', 'Creates Instant Connection', 'Encourages Sharing', 'Keeps the Brand Human'],
    weaknesses: ['Can Lack Seriousness', 'Humour Can Divide', 'Short-lived Attention', 'Risks Becoming Gimmicky'],
  },
  Magician: {
    tagline: {
      lineBold: "The Transformer.",
      lineItalic1: "It turns possibilities into reality.",
      lineItalic2: "It makes people believe anything is possible.",
    },
    stoodOut: [
      "You consistently chose transformation over transactions.",
      "You believe brands should inspire wonder and possibility.",
      "You value innovation, imagination and meaningful change.",
    ],
    traits: [
      { name: 'Visionary',      pct: 95 },
      { name: 'Imaginative',    pct: 90 },
      { name: 'Inspirational',  pct: 85 },
      { name: 'Charismatic',    pct: 80 },
      { name: 'Transformative', pct: 75 },
    ],
    strengths:  ['Inspires Possibility', 'Creates Extraordinary Experiences', 'Builds Emotional Fascination', 'Leads Through Innovation'],
    weaknesses: ['Can Feel Unrealistic', 'Difficult to Explain', 'High Customer Expectations', 'Risks Overpromising'],
  },
  Ruler: {
    tagline: {
      lineBold: "The Leader.",
      lineItalic1: "It sets the standard.",
      lineItalic2: "It inspires confidence through excellence.",
    },
    stoodOut: [
      "You consistently chose quality over compromise.",
      "You believe brands should lead with confidence and authority.",
      "You value excellence, prestige and lasting influence.",
    ],
    traits: [
      { name: 'Authoritative', pct: 95 },
      { name: 'Confident',     pct: 90 },
      { name: 'Sophisticated', pct: 85 },
      { name: 'Responsible',   pct: 80 },
      { name: 'Disciplined',   pct: 75 },
    ],
    strengths:  ['Commands Respect', 'Builds Premium Perception', 'Creates Customer Confidence', 'Sets Industry Standards'],
    weaknesses: ['Can Feel Intimidating', 'Risks Appearing Elitist', 'Slow to Adapt', 'Perceived as Controlling'],
  },
};

export const getResultContent = (archetypeName) =>
  archetypeResultContent[archetypeName] || archetypeResultContent.Everyman;
